export const PING_PROTOCOLS = [
  { key: 'address', label: 'IP', url: 'addressPingUrl', available: 'addressAvailable', probe: 'addressProbe' },
  { key: 'url', label: 'HTTP', url: 'urlPingUrl', available: 'urlAvailable', probe: 'urlProbe' },
  { key: 'https', label: 'HTTPS', url: 'httpsPingUrl', available: 'httpsAvailable', probe: 'httpsProbe' }
]

const OUTCOME_LABELS = {
  Success: 'Успех',
  SuccessInvalidJson: 'Некорректный JSON',
  HttpError: 'HTTP-ошибка',
  Timeout: 'Таймаут',
  Unreachable: 'Недоступен',
  Canceled: 'Отменён',
  Skipped: 'Пропущен',
  Exception: 'Ошибка'
}

const SHORT_OUTCOMES = new Set(['Timeout', 'Skipped', 'Unreachable'])

export function unwrapPingStatus(response) {
  if (response == null || response.error) {
    return null
  }

  const candidates = [response.data, response]
  for (const candidate of candidates) {
    if (isPingStatus(candidate)) {
      return candidate
    }
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      continue
    }
    const nested = Object.values(candidate).find(isPingStatus)
    if (nested) {
      return nested
    }
  }

  return response.data ?? response
}

function isPingStatus(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (
      value.divisionId != null ||
      value.online !== undefined ||
      value.addressProbe ||
      value.urlProbe ||
      value.httpsProbe ||
      value.failureReason !== undefined
    )
  )
}

export function getProbe(status, protocol) {
  return status?.[protocol.probe] || null
}

export function outcomeLabel(outcome) {
  return OUTCOME_LABELS[outcome] || outcome || ''
}

export function shortProbeReason(probe) {
  if (!probe) {
    return null
  }
  if (probe.outcome === 'HttpError' && probe.statusCode != null) {
    return ['HTTP', probe.statusCode, probe.reasonPhrase].filter(part => part !== '' && part != null).join(' ')
  }
  if (probe.httpRequestError) {
    return probe.httpRequestError
  }
  if (probe.socketError) {
    return probe.socketError
  }
  if (SHORT_OUTCOMES.has(probe.outcome)) {
    return probe.outcome
  }
  return probe.failureReason || null
}

export function probeHasProblem(probe, available) {
  if (probe) {
    return probe.available === false || (probe.outcome && probe.outcome !== 'Success')
  }
  return available === false
}

export function protocolBadgeTitle(status, protocol) {
  const probe = getProbe(status, protocol)
  const available = typeof probe?.available === 'boolean' ? probe.available : status?.[protocol.available]
  if (available !== false) {
    return undefined
  }
  return shortProbeReason(probe) || 'Недоступен'
}

export function probeStatusLabel(status, protocol) {
  const probe = getProbe(status, protocol)
  if (!probe) {
    if (status?.[protocol.available] === true) return 'доступен'
    if (status?.[protocol.available] === false) return 'недоступен'
    return 'нет данных'
  }
  const availability = probe.available ? 'доступен' : 'недоступен'
  const outcome = outcomeLabel(probe.outcome)
  return outcome ? `${availability} · ${outcome}` : availability
}

export function probeHttpStatus(probe) {
  if (probe?.statusCode == null && !probe?.reasonPhrase) {
    return null
  }
  return [probe.statusCode, probe.reasonPhrase].filter(part => part !== '' && part != null).join(' ')
}

export function failedProbeLabels(status) {
  return PING_PROTOCOLS
    .filter(protocol => probeHasProblem(getProbe(status, protocol), status?.[protocol.available]))
    .map(protocol => protocol.label)
}

export function pingResultToast(status, name) {
  const label = name ? `«${name}»` : 'площадки'
  if (!status || status.online === false) {
    return {
      type: 'error',
      message: status?.failureReason || `Проверка ${label} не удалась`
    }
  }

  const failed = failedProbeLabels(status)
  if (failed.length) {
    return {
      type: 'warning',
      message: `Проверка ${label} выполнена, проблемы: ${failed.join(', ')}`
    }
  }

  return {
    type: 'success',
    message: `Проверка ${label} выполнена`
  }
}

export function notifyPingResult(toast, status, name) {
  const { type, message } = pingResultToast(status, name)
  toast[type](message)
}
